/**
 * Sprint 23.5 — HireDraftField.
 *
 * The base row used for most draft fields on the right panel. Renders a
 * label, the current value (read mode) or an inline editor (edit mode), and
 * two affordances per the Q4 trio: pencil (edit directly) + sparkle
 * (regenerate via AI). The third affordance — full chat — is the surface
 * itself, so there's no button for it here.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export interface HireDraftFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  onChange: (value: string) => void;
  onRegenerate?: () => void;
  /** Optional fully-custom display body — when provided, replaces the value text. */
  display?: ReactNode;
  /** Hide the pencil (e.g. for tag editors that are always editable). */
  hideEdit?: boolean;
  /** Hide the sparkle (e.g. for fields the AI can't usefully regenerate). */
  hideRegenerate?: boolean;
  /** Optional disabled state for the regenerate button. */
  regenerateDisabled?: boolean;
}

export function HireDraftField({
  label,
  value,
  placeholder,
  multiline = false,
  onChange,
  onRegenerate,
  display,
  hideEdit,
  hideRegenerate,
  regenerateDisabled,
}: HireDraftFieldProps) {
  const [editing, setEditing] = useState(false);
  const [buffer, setBuffer] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // When the parent value changes mid-flight (AI patch), surface it in the buffer.
  useEffect(() => {
    if (!editing) setBuffer(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Put the caret at the end.
      const length = inputRef.current.value.length;
      try {
        inputRef.current.setSelectionRange(length, length);
      } catch {
        /* ignore for input types that don't support selection */
      }
    }
  }, [editing]);

  function commit() {
    setEditing(false);
    if (buffer !== value) onChange(buffer);
  }
  function cancel() {
    setEditing(false);
    setBuffer(value);
  }

  return (
    <div style={wrap}>
      <div style={labelRow}>
        <span style={labelText}>{label}</span>
        <div style={{ flex: 1 }} />
        {!hideEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={iconBtn}
            title={`Edit ${label.toLowerCase()}`}
            aria-label={`Edit ${label}`}
          >
            <i className="ti ti-pencil" style={{ fontSize: 12 }} aria-hidden="true" />
          </button>
        )}
        {!hideRegenerate && onRegenerate && !editing && (
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerateDisabled}
            style={iconBtn}
            title={`Regenerate ${label.toLowerCase()} with AI`}
            aria-label={`Regenerate ${label}`}
          >
            <i className="ti ti-sparkles" style={{ fontSize: 12 }} aria-hidden="true" />
          </button>
        )}
      </div>

      {editing ? (
        multiline ? (
          <textarea
            ref={(el) => {
              inputRef.current = el;
            }}
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                commit();
              }
            }}
            rows={3}
            style={textarea}
            placeholder={placeholder}
          />
        ) : (
          <input
            ref={(el) => {
              inputRef.current = el;
            }}
            type="text"
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
            style={input}
            placeholder={placeholder}
          />
        )
      ) : display ? (
        <div style={displayBox}>{display}</div>
      ) : value.trim().length > 0 ? (
        <div style={valueText}>{value}</div>
      ) : (
        <div style={emptyText}>{placeholder ?? "Not set"}</div>
      )}
    </div>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  paddingBottom: 10,
};

const labelRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  minHeight: 18,
};

const labelText: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const iconBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: 4,
};

const input: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "6px 8px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
};

const textarea: CSSProperties = {
  ...input,
  resize: "vertical",
  lineHeight: 1.5,
  fontFamily: "var(--font-sans)",
};

const valueText: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--color-text-primary)",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const emptyText: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  fontStyle: "italic",
};

const displayBox: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-primary)",
};
